import { CubeTextureLoader, FileLoader, Loader, Texture, TextureLoader } from 'three';
import { Dict } from '../utils/Dict';
import { Signal } from '../utils/Signal';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { AudioLoader } from './AudioLoader';

export type LoaderType = new () => Loader;
export type AssetType = Record<string, unknown> | string | Texture | AudioBuffer;
const defaultLoaders: Dict<LoaderType> = {
  'png': TextureLoader,
  'jpg': TextureLoader,
  'jpeg': TextureLoader,
  'glb': GLTFLoader,
  'glsl': FileLoader,
  'mp3': AudioLoader
};

function getDefaultLoader(file: string): LoaderType
{
  if (Array.isArray(file))
    return CubeTextureLoader;

  const ext = file.substring(file.lastIndexOf('.') + 1).toLowerCase();
  return defaultLoaders[ext];
}

/**
 * AssetLibrary allows loading a queue of assets.
 */
export default class Asset3DLibrary
{
  defaultTextureFlipY = true;
  expectedNumAssets = 0;

  readonly onComplete = new Signal<void>();
  readonly onProgress = new Signal<number>();
  readonly onError = new Signal<ErrorEvent>();
  readonly basePath: string = '';

  private _numLoaded = 0;
  private _numAssets = 0;
  private _progress = 0;
  private _assets: Dict<AssetType> = {};
  private _waitingForCompletion = false;
  private _queueEmpty = true;

  /**
     * Creates a new AssetLibrary object.
     * @param basePath The root directory under which all assets are located. Any filename used when queueing assets is
     * relative to this.
     */
  constructor(basePath?:string)
  {
    basePath = basePath || './';

    // make sure the path ends with a slash if it doesn't already
    if (basePath.charAt(basePath.length - 1) !== '/') basePath = basePath + '/';

    this.basePath = basePath;

    this._onAssetLoaded = this._onAssetLoaded.bind(this);
    this._onAssetError = this._onAssetError.bind(this);
    this._onAssetProgress = this._onAssetProgress.bind(this);
  }

  /**
     * Adds an asset to the load queue.
     * @param id The ID that can be used to fetch the assets from the library once loaded.
     * @param filename The filename (relative to the base path passed in the constructor) of the asset.
     * @param loaderType The type of loader to use. Fe: TextureLoader, FBXLoader, ... If not provided, an estimate will
     * @param onComplete A callback for this asset only.
     * be made based on the filename.
     */
  load(id: string, filename: string, loaderType?: LoaderType, onComplete?:(asset:AssetType) => void): void
  {
    if (this._waitingForCompletion)
      console.error('Cannot queue more loaders when waiting for completion!');

    this._queueEmpty = false;

    // cube map loaders require arrays
    if (Array.isArray(filename)) {
      for (let i = 0; i < filename.length; ++i) {
        filename[i] = this._completeFilename(filename[i]);
      }
    }
    else
      filename = this._completeFilename(filename);

    loaderType = loaderType || getDefaultLoader(filename);

    const loader: Loader = new loaderType();

    ++this._numAssets;

    loader.loadAsync(
      filename,
      this._onAssetProgress
    ).catch(err => {
      this._onAssetError(err);
    }).then(asset =>
    {
      if (onComplete) onComplete(asset);

      this._assets[id] = asset;
      this._onAssetLoaded(asset);
    });
  }

  /**
     * Returns the current loading progress.
     * @returns {*|number}
     */
  get progress(): number
  {
    return this._progress;
  }

  get numAssets(): number
  {
    return Math.max(this.expectedNumAssets, this._numAssets);
  }

  /**
     * Retrieves a loaded asset.
     * @param id The asset's ID as defined when adding to the queue.
     */
  get(id: string): AssetType
  {
    return this._assets[id];
  }

  /**
     * Explicitly set an asset with a given id.
     * @param id The asset's ID as defined when adding to the queue.
     * @param value The asset to associate with the ID.
     */
  set(id: string, value: AssetType): void
  {
    this._assets[id] = value;
  }

  waitForCompletion(): void
  {
    this._waitingForCompletion = true;

    if (this._queueEmpty) {
      this._progress = 1;
      this.onComplete.dispatch();
    }
  }

  private _onAssetLoaded(asset: AssetType): void
  {
    if (asset instanceof Texture)
      asset.flipY = this.defaultTextureFlipY;

    ++this._numLoaded;
    this.onProgress.dispatch(this._numLoaded / this.numAssets);

    if (this._numLoaded === this._numAssets) {
      this._queueEmpty = true;
      if (this._waitingForCompletion) {
        this._progress = 1;
        this.onComplete.dispatch();
      }
    }
  }

  private _onAssetProgress(event: ProgressEvent): void
  {
    const ratio = event.loaded / event.total;
    this._progress = (this._numLoaded + ratio) / this.numAssets;
    this.onProgress.dispatch(this._progress);
  }

  private _onAssetError(error: ErrorEvent): void
  {
    this.onError.dispatch(error);
  }

  private _completeFilename(filename: string): string
  {
    // using full url, should not prepend pase path.
    if (filename.indexOf('http:') < 0 && filename.indexOf('https:') < 0)
      filename = this.basePath + filename;

    return filename;
  }
}